import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createPlan, getPermsList } from "../services/PlanServices";
import { confirmPlanModelsToast, showToast } from "toast/toastHelper";
import { DisclosurePanel, Disclosure } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import StatusSwitch from "./BotSelect";
import MultiSelectReactSelect from "./MultiSelectReactSelect";
import { botList } from "units/admin/services/adminServices";
export const PermCategory = {
  PAGE: "page",
  API: "api",
  AI_MODEL: "ai",
  EMBEDDER: "textEmbder",
  CHART: "analysticChart",
  PLATFORM: "platform",
  TOOL: "ai-tool",
};

const buildOptionsByCategory = (data, category) =>
  data
    .filter((item) => item.category === category && item.isAvailable)
    .map((item) => ({
      value: item.id,
      label: item.persianName,
    }));

export default function PlanForm() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    days: "",
    tokens: "",
    files: "",
    fileSize: "",
    userLimit: "",
    off: "",
    active: undefined,
    messagePerDay: "",
    promptTokens: "",
    userTokens: "",
    fileTokens: "",
    gptMaxTokens: "",
    charLimit: "",
    isFeatured: false,
    platformPercent: 0,
    ragSlots: "",
    ragTokens: "",
  });
  // MultiSelect fields (Array)
  const [selectedOptions, setSelectedOptions] = useState({
    charts: [],
    languageModels: [],
    embeddingModels: [],
    tools: [],
    pageAccess: [],
    socialPlatform: [],
  });
  const [chartsOptions, setChartsOptions] = useState([]);
  const [languageModelsOptions, setLanguageModelsOptions] = useState([]);
  const [embeddingModelsOptions, setEmbeddingModelsOptions] = useState([]);
  const [toolsOptions, setToolsOptions] = useState([]);
  const [socialPlatformOptions, setSocialPlatformOptions] = useState([]);
  const [pageAccessOptions, setPageAccessOptions] = useState([]);

  const [botsEnabled, setBotsEnabled] = useState(false);
  const [botsOptions, setBotsOptions] = useState([]);
  const [selectedBots, setSelectedBots] = useState([]);
  const [loadingBots, setLoadingBots] = useState(false);
  const isOptionsLoading = !languageModelsOptions.length;

  const [errors, setErrors] = useState({});
  const accessToken = useSelector((state) => state.auth.accessToken);
  const navigate = useNavigate();
  const toEnglishDigits = (str) => {
    if (typeof str !== "string") str = String(str);
    return str
      .replace(/[\u200E\u200F]/g, "") // حذف کاراکترهای نامرئی RTL و LTR
      .replace(/[٬,،\s]/g, "") // حذف تمام کاماها و فاصله‌ها
      .replace(/[۰-۹]/g, (d) =>
        String.fromCharCode(d.charCodeAt(0) - 0x06f0 + 48)
      )
      .replace(/[٠-٩]/g, (d) =>
        String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48)
      )
      .replace(/−/g, "-");
  };
  const selectOptionsMap = {
    charts: chartsOptions,
    languageModels: languageModelsOptions,
    embeddingModels: embeddingModelsOptions,
    tools: toolsOptions,
    socialPlatform: socialPlatformOptions,
    pageAccess: pageAccessOptions,
  };

  // تبدیل اعداد انگلیسی به فارسی
  const toPersianDigits = (str) => {
    if (typeof str !== "string") return str; // اگر ورودی از نوع رشته نباشد، بدون تغییر بازگردانده می‌شود
    return str.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[+d]);
  };

  // اضافه کردن کامای فارسی
  const addCommas = (str) => {
    if (str === null || str === undefined || str === "") return null;
    return str.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "٬");
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const allowedNegativeOneFields = [
      "days",
      "messagePerDay",
      "charLimit",
      "fileSize",
      "files",
    ];
    const cleanedValue = toEnglishDigits(value).replace(/٬/g, "");

    // اگر فقط "-" وارد شده، اون رو پاک کن
    if (cleanedValue === "-" && allowedNegativeOneFields.includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: "" }));
    }

    // اگر عدد معتبری بود، فرمتش رو اصلاح کن و نمایش بده
    if (!isNaN(Number(cleanedValue)) && cleanedValue !== "") {
      const formattedDisplay = toPersianDigits(addCommas(cleanedValue));
      e.target.value = formattedDisplay;
    }
  };

  const allowedNegativeOneFields = [
    "days",
    "messagePerDay",
    "charLimit",
    "fileSize",
    "files",
  ]; // فیلدهایی که اجازه مقدار -1 دارند
  const discountRegex = /^(100|[1-9]?[0-9])$/;

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let cleanedValue = toEnglishDigits(value).replace(/٬/g, ""); // حذف کاما
    // اگر نام فیلد "name" باشد، تمام حروف را کوچک می‌کنیم
    if (name === "name") {
      setFormData((prev) => ({ ...prev, [name]: value.toLowerCase() }));
      return;
    }
    if (name === "active") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
      return;
    }
    if (name === "ragSlots" || name === "ragTokens") {
      if (cleanedValue === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: "",
        }));

        setErrors((prev) => ({
          ...prev,
          [name]: false,
        }));

        return;
      }

      // فقط عدد صحیح
      if (!/^\d+$/.test(cleanedValue)) {
        return;
      }

      const numberValue = Number(cleanedValue);

      // محدودیت RAG Slots: بین 5 تا 15
      if (name === "ragSlots") {
        if (numberValue < 5 || numberValue >= 20) {
          setErrors((prev) => ({
            ...prev,
            ragSlots: "تعداد رگ باید بین ۵ تا 20 باشد",
          }));

          setFormData((prev) => ({
            ...prev,
            ragSlots: cleanedValue,
          }));

          return;
        }
      }

      // محدودیت RAG Tokens: بین 4000 تا 12000
      if (name === "ragTokens") {
        if (numberValue < 4000 || numberValue > 12000) {
          setErrors((prev) => ({
            ...prev,
            ragTokens: "تعداد توکن رگ باید بین ۴۰۰۰ تا ۱۲۰۰۰ باشد",
          }));

          setFormData((prev) => ({
            ...prev,
            ragTokens: cleanedValue,
          }));

          return;
        }
      }

      // مقدار معتبر است
      setFormData((prev) => ({
        ...prev,
        [name]: cleanedValue,
      }));

      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));

      return;
    }
    if (name === "isFeatured") {
      setFormData((prev) => ({ ...prev, isFeatured: e.target.checked }));
      return;
    }
    // برای چک‌باکس‌ها و رادیو دکمه‌ها هیچ کاری انجام نمی‌دهیم
    if (type === "radio") {
      return;
    }
    if (name === "platformPercent") {
      if (cleanedValue === "") {
        setFormData((prev) => ({ ...prev, platformPercent: "" }));
        return;
      }

      // فقط عدد صحیح
      if (!/^\d*$/.test(cleanedValue)) {
        return;
      }

      // حداکثر 100 درصد
      const numberValue = Number(cleanedValue);

      if (numberValue > 100) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        platformPercent: cleanedValue,
      }));

      e.target.value = toPersianDigits(cleanedValue);
      return;
    }
    if (name === "off") {
      if (cleanedValue === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      if (!/^(100|[1-9]?[0-9])$/.test(cleanedValue)) {
        return; // جلوگیری از وارد کردن مقدار نامعتبر
      }

      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
      const formattedDisplay = toPersianDigits(addCommas(cleanedValue));
      e.target.value = formattedDisplay;
      return;
    }
    // تمیز کردن مقدار ورودی
    // let cleanedValue = toEnglishDigits(value).replace(/٬/g, ""); // حذف کاما

    // اگر -1 باشد و فیلد مجاز باشد، آن را بپذیریم
    if (cleanedValue === "-1" && allowedNegativeOneFields.includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
      return;
    }

    // برای فیلدهای غیرمجاز، از وارد کردن -1 جلوگیری می‌کنیم
    if (cleanedValue === "-1") {
      e.target.value = ""; // مقدار را پاک می‌کنیم
      return;
    }

    // بررسی برای اینکه مقدار وارد شده فقط عددی باشد
    if (!/^\d*$/.test(cleanedValue)) {
      return; // اگر مقدار غیرمجاز باشد، هیچ چیزی ذخیره نمی‌شود
    }

    // ذخیره‌ی عدد تمیز شده در formData
    setFormData((prev) => ({ ...prev, [name]: cleanedValue }));

    // فقط در صورتی که مقدار منفی نیست (یعنی -1 نیست)، کاما اضافه می‌کنیم
    if (cleanedValue !== "-") {
      const formattedDisplay = toPersianDigits(addCommas(cleanedValue));
      e.target.value = formattedDisplay;
    }
  };

  const cleanNumber = (val) => {
    const cleanedVal = toEnglishDigits(val).replace(/٬/g, "");
    if (cleanedVal === "-1") return -1; // اگر مقدار -1 بود، آن را به عدد -1 تبدیل کن
    return isNaN(Number(cleanedVal)) ? "" : Number(cleanedVal); // بررسی اینکه آیا عدد معتبر است
  };
  // multi select box
  useEffect(() => {
    const fetchPlanOptions = async () => {
      try {
        const res = await getPermsList(accessToken);
        if (res?.ok) {
          const data = res?.data;
          setChartsOptions(buildOptionsByCategory(data, PermCategory.CHART));
          setLanguageModelsOptions(
            buildOptionsByCategory(data, PermCategory.AI_MODEL)
          );
          setEmbeddingModelsOptions(
            buildOptionsByCategory(data, PermCategory.EMBEDDER)
          );
          setToolsOptions(buildOptionsByCategory(data, PermCategory.TOOL));
          setSocialPlatformOptions(
            buildOptionsByCategory(data, PermCategory.PLATFORM)
          );
          setPageAccessOptions(buildOptionsByCategory(data, PermCategory.PAGE));
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchPlanOptions();
  }, []);

  const handleSelectBoxChange = (e) => {
    const { name, value } = e.target;

    setSelectedOptions((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  // bot list api
  useEffect(() => {
    if (!botsEnabled) {
      setSelectedBots([]);
      return;
    }

    const fetchBots = async () => {
      setLoadingBots(true);
      try {
        const res = await botList(accessToken);

        setBotsOptions(
          res?.data?.map((bot) => ({
            value: bot.id,
            label: bot.username,
          }))
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingBots(false);
      }
    };

    fetchBots();
  }, [botsEnabled]);

  const mapSelectToValues = (arr = []) => arr.map((item) => item.value);
  const submitPlan = async (permissions) => {
    try {
      const res = await createPlan({
        name: formData?.name,
        price: cleanNumber(formData?.price),
        days: cleanNumber(formData?.days),
        tokens: cleanNumber(formData?.tokens),
        files: cleanNumber(formData?.files),
        fileSize: cleanNumber(formData?.fileSize),
        userLimit: cleanNumber(formData?.userLimit),
        off: cleanNumber(formData?.off),
        platformPercent: cleanNumber(formData?.platformPercent),
        userTokens:
          formData?.userTokens !== ""
            ? cleanNumber(formData?.userTokens)
            : null,
        charLimit:
          formData?.charLimit !== "" ? cleanNumber(formData?.charLimit) : null,
        messagePerDay:
          formData?.messagePerDay !== ""
            ? cleanNumber(formData?.messagePerDay)
            : null,
        gptMaxTokens:
          formData?.gptMaxTokens !== ""
            ? cleanNumber(formData?.gptMaxTokens)
            : null,
        fileTokens:
          formData?.fileTokens !== ""
            ? cleanNumber(formData?.fileTokens)
            : null,
        promptTokens:
          formData?.promptTokens !== ""
            ? cleanNumber(formData?.promptTokens)
            : null,
        ragSlots: cleanNumber(formData?.ragSlots),
        ragTokens: cleanNumber(formData?.ragTokens),
        active: formData?.active,
        isFeatured: formData?.isFeatured,
        permissions,
        bots: botsEnabled ? mapSelectToValues(selectedBots) : null,
        accessToken,
      });

      if (res?.ok) {
        setFormData({
          name: "",
          price: "",
          days: "",
          tokens: "",
          files: "",
          fileSize: "",
          userLimit: "",
          off: "",
          promptTokens: "",
          messagePerDay: "",
          charLimit: "",
          userTokens: "",
          fileTokens: "",
          gptMaxTokens: "",
          isFeatured: false,
          active: true,
          ragSlots: "",
          ragTokens: "",
        });

        setSelectedOptions({
          charts: [],
          languageModels: [],
          embeddingModels: [],
          tools: [],
          pageAccess: [],
          socialPlatform: [],
        });

        setSelectedBots([]);
        setBotsEnabled(false);

        showToast("طرح با موفقیت ایجاد شد.", "success");
        navigate("/admin/plans");
      }
    } catch (error) {
      showToast(error || "خطا در ارسال:", "error");
      console.log("خطا در ارسال:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const newErrors = {};

    if (
      formData.ragSlots === "" ||
      formData.ragSlots === null ||
      formData.ragSlots === undefined
    ) {
      newErrors.ragSlots = "تعداد RAG الزامی است";
    }

    if (
      formData.ragTokens === "" ||
      formData.ragTokens === null ||
      formData.ragTokens === undefined
    ) {
      newErrors.ragTokens = "تعداد توکن RAG الزامی است";
    }

    if (formData.ragSlots !== "" && Number(formData.ragSlots) < 0) {
      newErrors.ragSlots = "تعداد RAG نمی‌تواند منفی باشد";
    }

    if (formData.ragTokens !== "" && Number(formData.ragTokens) < 0) {
      newErrors.ragTokens = "تعداد توکن RAG نمی‌تواند منفی باشد";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }
    const optionalFields = [
      "off",
      "messagePerDay",
      "promptTokens",
      "userTokens",
      "fileTokens",
      "gptMaxTokens",
      "charLimit",
    ];

    /*
    const requiredFields = Object.entries(formData).filter(
      ([key, val]) =>
        !optionalFields.includes(key) &&
        (val === "" || val === null || val === undefined || /^\s*$/.test(val))
    );

    if (requiredFields.length > 0) {
      const newErrors = {};
      requiredFields.forEach(([key]) => {
        newErrors[key] = true;
      });
      setErrors(newErrors);
      setLoading(false);
      return;
    }
    */

    setErrors({});
    const permissionsArray = [
      ...mapSelectToValues(selectedOptions.charts),
      ...mapSelectToValues(selectedOptions.languageModels),
      ...mapSelectToValues(selectedOptions.embeddingModels),
      ...mapSelectToValues(selectedOptions.tools),
      ...mapSelectToValues(selectedOptions.pageAccess),
      ...mapSelectToValues(selectedOptions.socialPlatform),
    ];
    const permissions = permissionsArray.length > 0 ? permissionsArray : null;

    if (
      selectedOptions.languageModels.length === 0 ||
      selectedOptions.embeddingModels.length === 0
    ) {
      setLoading(false);

      return confirmPlanModelsToast(() => {
        setLoading(true);
        submitPlan(permissions);
      });
    }

    await submitPlan(permissions);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-12 ">
        <div className=" pb-2">
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label
                htmlFor="name"
                className="block text-sm/6 font-medium text-gray-900"
              >
                نام طرح
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  value={formData?.name}
                  placeholder="رایاپک..."
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 
                                    -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#aadec5] sm:text-sm/6"
                />
              </div>
              {errors.name && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <StatusSwitch value={botsEnabled} onChange={setBotsEnabled} />

              <div
                className={`transition-all duration-300 ease-in-out
                                     ${botsEnabled
                    ? "opacity-100 translate-y-0 mt-4 pointer-events-auto"
                    : "opacity-0 -translate-y-2 pointer-events-none"
                  }`}
              >
                {botsEnabled && (
                  <div className=" ">
                    <label className="mb-2 block text-sm text-gray-700">
                      لیست بات ها
                    </label>

                    <MultiSelectReactSelect
                      name="bots"
                      value={selectedBots}
                      options={botsOptions}
                      onChange={(e) => setSelectedBots(e.target.value)}
                      isDisabled={loadingBots}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="tokens"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تعداد توکن های اختصاص داده شده به طرح
              </label>
              <div className="mt-2">
                <input
                  id="tokens"
                  name="tokens"
                  value={addCommas(formData?.tokens)}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border border-gray-300 placeholder:text-gray-400 
                                      focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              {errors.tokens && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>

            <div className="sm:col-span-3">
              <label
                htmlFor="files"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تعداد فایل مجاز برای آپلود
              </label>
              <div className="mt-2">
                <input
                  id="files"
                  name="files"
                  value={addCommas(formData?.files)}
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 
                                    -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *برای تعداد فایل نامحدود، مقدار -1 را وارد کنید.
              </small>
              <br />
              {errors.files && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="fileSize"
                className="block text-sm/6 font-medium text-gray-900"
              >
                حجم مجاز هر فایل بارگذاری شده
              </label>
              <div className="mt-2">
                <input
                  id="fileSize"
                  name="fileSize"
                  value={addCommas(formData?.fileSize)}
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                * حجم هر فایل برحسب مگابایت می باشد(برای حجم نامحدود، مقدار -1
                را وارد کنید.).
              </small>
              <br />
              {errors.fileSize && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="userLimit"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تعداد کاربر
              </label>
              <div className="mt-2">
                <input
                  id="userLimit"
                  name="userLimit"
                  value={addCommas(formData?.userLimit)}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              {errors.userLimit && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="price"
                className="block text-sm/6 font-medium text-gray-900"
              >
                قیمت طرح
              </label>
              <div className="mt-2">
                <input
                  id="price"
                  name="price"
                  value={addCommas(formData?.price)}
                  type="text"
                  onChange={handleChange}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                * قیمت طرح به ریال می باشد.
              </small>
              <br />
              {errors.price && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>

            <div className="sm:col-span-3">
              <label
                htmlFor="days"
                className="block text-sm/6 font-medium text-gray-900"
              >
                مدت زمان اعتبار طرح
              </label>
              <div className="mt-2">
                <input
                  id="days"
                  name="days"
                  value={addCommas(formData?.days)}
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="...روز"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *تعداد روزهای اعتبار طرح را وارد کنید (برای روزهای نامحدود،
                مقدار -1 را وارد کنید).
              </small>
              <br />
              {errors.days && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="sm:col-span-3">
              <label
                htmlFor="off"
                className="block text-sm/6 font-medium text-gray-900"
              >
                تخفیف
              </label>
              <div className="mt-2">
                <input
                  id="off"
                  name="off"
                  value={formData?.off}
                  type="text"
                  onChange={handleChange}
                  placeholder=""
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *میزان تخفیف مورد نظر را وارد کنید.
              </small>
              <label
                htmlFor="off"
                className="block text-sm/6 font-medium mt-4 text-gray-900"
              >
                درصد کارمزد پلتفرم
              </label>
              <div className="mt-2">
                <input
                  id="platformPercent"
                  name="platformPercent"
                  value={formData?.platformPercent}
                  type="text"
                  onChange={handleChange}
                  placeholder=""
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5] sm:text-sm/6"
                />
              </div>
              <small className="mt-1 text-gray-600">
                *درصد پلتفرم از درآمد مدیر کسب و کار از پرداختی های مشتری را وارد کنید.
              </small>
            </div>
            <div className="sm:col-span-3">
              <fieldset className="flex flex-row items-center gap-6">
                <label className="shrink-0 text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {" "}
                  وضعیت طرح
                </label>
                {/* <p className="mt-1 text-sm/6 text-gray-600">These are delivered via SMS to your mobile phone.</p> */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <input
                      id="true-Status"
                      name="active"
                      type="radio"
                      value="true"
                      checked={formData?.active === true}
                      onChange={handleChange}
                      className="size-4 rounded-full border border-gray-300 bg-white appearance-none checked:bg-green-500 checked:border-green-500 checked:ring-2 checked:ring-green-500"
                    />
                    <label
                      htmlFor="true-Status"
                      className="inline-block text-sm/6 font-medium text-gray-900"
                    >
                      فعال
                    </label>
                  </div>
                  <div className="flex items-center gap-x-3">
                    <input
                      id="false-status"
                      name="active"
                      type="radio"
                      value="false"
                      checked={formData?.active === false}
                      onChange={handleChange}
                      className="size-4 rounded-full border border-gray-300 bg-white appearance-none checked:bg-green-500 checked:border-green-500 checked:ring-2 checked:ring-green-500"
                    />
                    <label
                      htmlFor="false-status"
                      className="inline-block text-sm/6 font-medium text-gray-900"
                    >
                      غیرفعال
                    </label>
                  </div>
                </div>
              </fieldset>
              {errors.active && (
                <p className="text-red-500 text-sm">این فیلد الزامی است</p>
              )}
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="isFeatured"
                  name="isFeatured"
                  checked={formData?.isFeatured}
                  onChange={handleChange}
                  className="
            size-4 cursor-pointer rounded
            border border-gray-300
            checked:hover:border-transparent
         checked:hover:bg-green-600
         checked:focus:border-green-600
         checked:focus:bg-green-600
            accent-green-600
            checked:bg-green-600 checked:border-green-600

            focus:outline-none
            focus:ring-2 focus:ring-green-300
            focus:ring-offset-0
            focus-visible:ring-green-300

            active:ring-0 active:outline-none active:bg-green-600
            hover:ring-0

            transition-all duration-150
        "
                />
                <span className="text-sm font-medium text-gray-900">
                  طرح برگزیده
                </span>
              </label>
              <small className="mt-3 text-gray-600">
                *با فعالسازی این گزینه ، این طرح در صفحه اصلی وبسایت نمایش داده میشود.
              </small>
            </div>
          </div>
        </div>

        <div className="sm:col-span-3">
          <Disclosure as="div" className="border-t border-gray-200 px-4 py-6">
            {({ open }) => (
              <>
                <h3 className="-mx-2 -my-3 flow-root">
                  <Disclosure.Button className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                    <span className="font-medium text-gray-900">
                      تنظیمات پیشرفته
                    </span>
                    <span className="ml-6 flex items-center">
                      {open ? (
                        <MinusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      ) : (
                        <PlusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      )}
                    </span>
                  </Disclosure.Button>
                </h3>

                <Disclosure.Panel className="pt-6">
                  <div className="space-y-6">
                    {[
                      {
                        label: "حداکثر توکن ارسالی  از کاربر (دریافتی GPT)",
                        name: "userTokens",
                        value: addCommas(formData?.userTokens),
                      },
                      {
                        label: "حداکثر توکن دریافتی  به کاربر (ارسالی GPT)",
                        name: "gptMaxTokens",
                        value: addCommas(formData?.gptMaxTokens),
                      },
                      {
                        label: " حداکثر کاراکتر مصرفی مجاز",
                        name: "charLimit",
                        value: addCommas(formData?.charLimit),
                      },
                      {
                        label: "تعداد پیام روزانه مجاز(هر ارسال - دریافت)",
                        name: "messagePerDay",
                        value: addCommas(formData?.messagePerDay),
                      },
                      {
                        label: "حداکثر توکن مجاز پرامپت سیستمی ",
                        name: "promptTokens",
                        value: addCommas(formData?.promptTokens),
                      },
                      {
                        label: "حداکثر توکن مجاز برای بارگذاری هر فایل",
                        name: "fileTokens",
                        value: addCommas(formData?.fileTokens),
                      },
                      {
                        label: "تعداد رگ های جمع آوری اطلاعات به هوش مصنوعی",
                        name: "ragSlots",
                        value: addCommas(formData?.ragSlots),
                      },
                      {
                        label: "تعداد توکن های مجاز مصرفی رگ برای ارسال اطلاعات به هوش مصنوعی",
                        name: "ragTokens",
                        value: addCommas(formData?.ragTokens),
                      },
                    ].map((item) => (
                      <div
                        key={item.name}
                        className="flex flex-row items-center justify-between gap-2"
                      >
                        <div className="flex flex-col  justify-start items-start ">
                          <label
                            htmlFor={item.name}
                            className="text-gray-600 text-sm "
                          >
                            {item.label}
                          </label>
                          {"messagePerDay".includes(item.name) && (
                            <small className="mt-1 text-gray-600">
                              *برای پیام روزانه ی نامحدود، مقدار -1 را وارد
                              کنید.
                            </small>
                          )}
                          {"charLimit".includes(item.name) && (
                            <small className="mt-1 text-gray-600">
                              *برای کاراکتر مصرفی نامحدود، مقدار -1 را وارد
                              کنید.
                            </small>
                          )}
                        </div>
                        <div className="flex flex-col  justify-end items-end ">
                          <input
                            id={item.name}
                            name={item.name}
                            type="text"
                            value={item.value}
                            onChange={handleChange}
                            onBlur={
                              ["charLimit", "messagePerDay"].includes(item.name)
                                ? handleBlur
                                : undefined
                            }
                            className=" w-[300px] rounded-md bg-white px-4 py-2 text-gray-900 text-sm outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-[#aadec5] focus:ring-2 focus:ring-[#aadec5]"
                          />
                          {errors[item.name] && (
                            <p className="mt-1 text-sm text-red-500">
                              {errors[item.name]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>

        <div className="sm:col-span-3">
          <Disclosure as="div" className="border-t border-gray-200 px-4 py-6">
            {({ open }) => (
              <>
                <h3 className="-mx-2 -my-3 flow-root">
                  <Disclosure.Button className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                    <span className="font-medium text-gray-900">
                      امکانات رایابات{" "}
                    </span>
                    <span className="ml-6 flex items-center">
                      {open ? (
                        <MinusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      ) : (
                        <PlusIcon
                          aria-hidden="true"
                          className="size-5 text-gray-400"
                        />
                      )}
                    </span>
                  </Disclosure.Button>
                </h3>
                <Disclosure.Panel className="pt-6">
                  <div className="space-y-6">
                    {[
                      {
                        label: "پلتفرم شبکه های اجتماعی",
                        name: "socialPlatform",
                      },
                      { label: "نمودارها", name: "charts" },
                      { label: "مدل های زبانی", name: "languageModels" },
                      { label: "مدل های برداری", name: "embeddingModels" },
                      { label: "ابزارها", name: "tools" },
                      { label: "دسترسی به صفحات", name: "pageAccess" },
                    ].map((item) => (
                      <div
                        key={item.name}
                        className="flex flex-row items-center justify-between gap-2"
                      >
                        {/* Label */}
                        <div className="flex flex-col items-start">
                          <label className="text-gray-600 text-sm">
                            {item.label}
                          </label>
                        </div>

                        {/* Multi Select */}
                        <div className="flex items-end">
                          <MultiSelectReactSelect
                            isDisabled={isOptionsLoading}
                            name={item.name}
                            value={selectedOptions[item.name]}
                            options={selectOptionsMap[item.name]}
                            onChange={handleSelectBoxChange}
                            className="w-[300px]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>

      <div className="my-4 flex items-center justify-center gap-x-6 ">
        <button
          type="submit"
          className="w-full rounded-md bg-[#aadec5] px-10 py-2 text-sm font-semibold text-gray-600 shadow-xs hover:bg-[#93dcb8] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#aadec5]"
          onClick={handleSubmit}
        >
          ثبت
        </button>
      </div>
    </form>
  );
}
